/* eslint-disable react-native/no-inline-styles */
// NOTE: This component is used on *WEB* only!

import { useSideDrawerLeftPresenter } from '@Components/SideDrawerLeft/SideDrawerLeftPresenter';
import { IS_SMALL_SCREEN } from '@Constants/LeftDrawer';
import useCheckoutPresenter from '@Screens/Checkout/presenter';
import { ScrollViewMarker } from '@Utils/scroll-view-manager/scroll-view-marker';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components/native';

import { Switch } from '../../../../Common/Switch';
import { Text } from '../../../../Common/Text';
import TextInput from '../../../../Common/TextInput';
import GooglePlacesInput from '../../../../GoogleInput';
import { defaultInputProps } from '../index';
import { defaultAddressInputStyle } from './CollectDeliveryAddress';
import StyledCardElement from './StyledCardElement';

import { useCheckoutStore } from '~stores/checkout';

const StyledDeliveryDetailsContainer = styled.View`
  width: 100%;
  background-color: white;
  border-radius: 27px;
  padding: ${IS_SMALL_SCREEN ? '25px 25px' : '35px 40px'};
  margin: 20px 0;
`;

const StyledInputLabel = styled(Text)`
  width: 100%;
  margin-left: 10px;
`;

const StyledCheckbox = styled.View`
  flex-direction: row;
  align-items: center;
  margin: 15px 0 25px 0;
`;

const StyledSwitchContainer = styled.View`
  margin-right: 10px;
  padding-left: 3px;
`;

const StyledDeliveryAddressContainer = styled.View``;

type Props = {
  checkoutIsSubmitting: boolean;
};

const CollectPaymentAddress: FC<Props> = ({ checkoutIsSubmitting }) => {
  const {
    checkoutBillingAddress,
    setCheckoutBillingAddressField,
    checkoutDeliveryAddress,
    setCheckoutDeliveryAddressField,
    setUseDeliveryAddressForBilling,
    useDeliveryAddressForBilling,
    _onCancelCheckoutButton,
    shouldSaveCard,
    setShouldSaveCard,
    shouldSaveCardDefault,
    setShouldSaveCardDefault,
    currentCheckoutMode,
    setCardDisplayName,
    cardDisplayName,
  } = useCheckoutStore();

  const {
    getBillingAddress,
    processOrderViaSingletonClass,
    getBorderColor,
    scrollViewManager,
  } = useCheckoutPresenter();

  const { activeProductId: productId } = useSideDrawerLeftPresenter();

  const previousProductId = useRef(productId);

  useEffect(() => {
    if (productId !== previousProductId.current) {
      _onCancelCheckoutButton();
      previousProductId.current = productId;
    }
  }, [productId]);

  return (
    <>
      <ScrollViewMarker manager={scrollViewManager} autoFocus />
      <StyledDeliveryDetailsContainer>
        <StyledCardElement />
        <TextInput
          {...defaultInputProps}
          value={checkoutBillingAddress?.recipientName ?? ''}
          placeholder='Name on card'
          textAlign='left'
          onChange={(text) => {
            setCheckoutBillingAddressField('recipientName', text);
            processOrderViaSingletonClass().then();
          }}
          customStyles={{
            borderWidth: 1,
          }}
          borderBottomColor={getBorderColor('PAYMENT_CARDHOLDER_NAME')}
          autocomplete='off'
        />

        <StyledCheckbox>
          <StyledSwitchContainer>
            <Switch
              isSwitchOn={useDeliveryAddressForBilling}
              onChange={() => {
                setUseDeliveryAddressForBilling(!useDeliveryAddressForBilling);
              }}
              color='black'
            />
          </StyledSwitchContainer>
          <StyledInputLabel size='s' weight='bold' align='left' color='black'>
            Use delivery address
          </StyledInputLabel>
        </StyledCheckbox>

        <StyledDeliveryAddressContainer
          pointerEvents={!useDeliveryAddressForBilling ? 'auto' : 'none'}
          style={{
            opacity: useDeliveryAddressForBilling ? 0.5 : 1,
          }}
        >
          <GooglePlacesInput
            onSelect={getBillingAddress}
            enabled={!useDeliveryAddressForBilling}
            styles={defaultAddressInputStyle()}
          />
          <TextInput
            {...defaultInputProps}
            textAlign='left'
            value={
              useDeliveryAddressForBilling
                ? checkoutDeliveryAddress?.street
                : checkoutBillingAddress?.street ?? ''
            }
            placeholder='Street'
            onChange={(text) => {
              if (useDeliveryAddressForBilling) {
                setCheckoutDeliveryAddressField('street', text);
              }
              setCheckoutBillingAddressField('street', text);
              processOrderViaSingletonClass().then();
            }}
            customStyles={{
              borderWidth: 1,
            }}
            borderBottomColor={getBorderColor('PAYMENT_CARDHOLDER_STREET')}
            editable={!checkoutIsSubmitting && !useDeliveryAddressForBilling}
            id='street-address'
            name='street-address'
            autocomplete='street-address'
          />

          <TextInput
            {...defaultInputProps}
            textAlign='left'
            value={
              useDeliveryAddressForBilling
                ? checkoutDeliveryAddress?.townOrCity
                : checkoutBillingAddress?.townOrCity ?? ''
            }
            placeholder='Town / city'
            onChange={(text) => {
              if (useDeliveryAddressForBilling) {
                setCheckoutDeliveryAddressField('townOrCity', text);
              }
              setCheckoutBillingAddressField('townOrCity', text);
              processOrderViaSingletonClass().then();
            }}
            customStyles={{
              borderWidth: 1,
            }}
            borderBottomColor={getBorderColor('PAYMENT_CARDHOLDER_TOWN')}
            editable={!checkoutIsSubmitting && !useDeliveryAddressForBilling}
            id='town'
            name='town'
            autocomplete='address-level2'
          />

          <TextInput
            {...defaultInputProps}
            textAlign='left'
            value={
              useDeliveryAddressForBilling
                ? checkoutDeliveryAddress?.county
                : checkoutBillingAddress?.county ?? ''
            }
            placeholder='County'
            onChange={(text) => {
              if (useDeliveryAddressForBilling) {
                setCheckoutDeliveryAddressField('county', text);
              }
              setCheckoutBillingAddressField('county', text);
              processOrderViaSingletonClass().then();
            }}
            editable={!checkoutIsSubmitting && !useDeliveryAddressForBilling}
            id='state'
            name='state'
            autocomplete='address-level1'
          />

          <TextInput
            {...defaultInputProps}
            textAlign='left'
            value={
              useDeliveryAddressForBilling
                ? checkoutDeliveryAddress?.postcode
                : checkoutBillingAddress?.postcode ?? ''
            }
            placeholder='Postcode'
            onChange={(text) => {
              if (useDeliveryAddressForBilling) {
                setCheckoutDeliveryAddressField('postcode', text);
              }
              setCheckoutBillingAddressField('postcode', text);
              processOrderViaSingletonClass().then();
            }}
            borderBottomColor={getBorderColor('PAYMENT_CARDHOLDER_POSTCODE')}
            editable={!checkoutIsSubmitting && !useDeliveryAddressForBilling}
            id='postal-code-billing'
            name='postal-code'
            autocomplete='postal-code'
          />
        </StyledDeliveryAddressContainer>
        {currentCheckoutMode === 'authed' && (
          <StyledCheckbox>
            <StyledSwitchContainer>
              <Switch
                isSwitchOn={shouldSaveCard}
                onChange={() => {
                  setShouldSaveCard(!shouldSaveCard);
                }}
                color='black'
              />
            </StyledSwitchContainer>
            <StyledInputLabel size='s' weight='bold' align='left' color='black'>
              Save this card
            </StyledInputLabel>
          </StyledCheckbox>
        )}
        {shouldSaveCard && currentCheckoutMode === 'authed' && (
          <>
            <TextInput
              {...defaultInputProps}
              textAlign='left'
              value={cardDisplayName}
              placeholder='Card name e.g: My Card'
              onChange={(value) => setCardDisplayName(value)}
              borderBottomColor={getBorderColor('CARD_DISPLAY_NAME')}
              editable
              id='address-name'
              name='address-name'
              autocomplete='name'
            />
            <StyledCheckbox>
              <StyledSwitchContainer>
                <Switch
                  isSwitchOn={shouldSaveCardDefault}
                  onChange={() =>
                    setShouldSaveCardDefault(!shouldSaveCardDefault)
                  }
                  color='black'
                />
              </StyledSwitchContainer>
              <StyledInputLabel
                size='s'
                weight='bold'
                align='left'
                color='black'
              >
                Make default card
              </StyledInputLabel>
            </StyledCheckbox>
          </>
        )}
      </StyledDeliveryDetailsContainer>
    </>
  );
};

export default observer(CollectPaymentAddress);
